import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { siteNoteText } from './notes'

import { api } from './api'
import Button from './Button.jsx'
import ConfirmDialog from './ConfirmDialog.jsx'
import Accounts from './Accounts.jsx'
import Calendar from './Calendar.jsx'
import Campaign from './Campaign.jsx'
import Posts from './Posts.jsx'
import Profile from './Profile.jsx'
import ReadingBar from './ReadingBar.jsx'
import Analytics from './Analytics.jsx'
import { AnalyticsIcon, CalendarIcon, ChatIcon, PostsIcon, ProfileIcon } from './Icons.jsx'
import { SUPPORTED_LANGUAGES } from './i18n'

/** Statuses that mean the read is still running, so the list keeps refreshing. */
const RUNNING = new Set(['Pending', 'Ingesting'])

/**
 * The signed-in app: a slim bar on top, the four places to be along the bottom, and the
 * conversation in the middle of it all.
 *
 * The chat is the product; everything administrative lives one tab away in Profile. Nothing about
 * the account deserves a card above the work.
 */
export default function Dashboard({ session, config, onSignedOut, onSessionChanged }) {
  const { t, i18n } = useTranslation()
  const [sites, setSites] = useState([])
  const [tenants, setTenants] = useState([])
  const [url, setUrl] = useState('')
  const [check, setCheck] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [calendarToken, setCalendarToken] = useState(0)
  const [postsToken, setPostsToken] = useState(0)
  const [tab, setTab] = useState('chat')

  // What the owner is working on, held here rather than in the chat: it is set from the post list
  // and from the calendar as well, and it has to survive the tab it was set from being left.
  const [focus, setFocus] = useState(null)

  // "Кажи му на дизајнерот" from any sheet: put the post on the table and go to the one chat.
  const askDesigner = useCallback((post) => {
    setFocus({ id: post.id, name: post.products?.[0]?.name ?? null })
    setTab('chat')
  }, [])
  // Bumped when a website finishes being read, so the conversation can carry on by itself.
  const [siteRead, setSiteRead] = useState(0)
  const [verification, setVerification] = useState(null)

  // Editors read the catalog but do not decide which website it comes from.
  const canManage = session.tenant.role === 'Owner' || session.tenant.role === 'Admin'

  const refresh = useCallback(async () => {
    try {
      setSites(await api.sites())
    } catch (failure) {
      setError(failure.message)
    }
  }, [])

  // The number in the corner is the one thing on this screen that other screens change: publishing
  // spends a credit, buying room spends a credit, a purchase adds them. It used to be read once at
  // sign-in and then quietly go stale — the owner watched twenty credits become nineteen only by
  // reloading the page.
  const refreshSession = useCallback(async () => {
    try {
      onSessionChanged(await api.me())
    } catch {
      // A session that cannot be re-read is a session about to end anyway; the next call says so.
    }
  }, [onSessionChanged])

  useEffect(() => {
    refresh()
    api.tenants().then(setTenants).catch(() => setTenants([]))
  }, [refresh])

  // A site read takes a minute or two, so the list follows it rather than making anyone reload.
  useEffect(() => {
    if (!sites.some((site) => RUNNING.has(site.status))) return undefined

    const timer = setInterval(refresh, 5000)
    return () => clearInterval(timer)
  }, [sites, refresh])

  // Which action is running, by name, so the spinner appears on the control that was pressed
  // rather than on all of them at once.
  const [working, setWorking] = useState(null)

  async function run(action, as = 'busy') {
    setBusy(true)
    setWorking(as)
    setError(null)
    try {
      await action()
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
      setWorking(null)
    }
  }

  const checkSite = () => run(async () => setCheck(await api.checkSite(url)), 'check')

  const addSite = () =>
    run(async () => {
      await api.addSite(url)
      setUrl('')
      setCheck(null)
      await refresh()
    }, 'add')

  const signOut = () =>
    run(async () => {
      await api.logout()
      onSignedOut()
    }, 'signOut')

  return (
    <div className="app">
      {/* Who and where, in one thin line. The work gets the screen. */}
      <header className="topbar">
        <img className="topbar__logo" src="/logo.png" alt="Dosegni" />
        <span className="topbar__meta">
          {session.tenant.name} · {t('dashboard.credits', { count: session.credits })}
        </span>
      </header>

      <main
        className={`app__main${tab === 'chat' ? ' app__main--chat' : ''}${
          tab === 'profile' ? ' app__main--settings' : ''
        }`}
      >
        {tab === 'chat' && canManage && (
          <Campaign
            onPostsWritten={() => {
              setPostsToken((token) => token + 1)
              refreshSession()
            }}
            onShowCalendar={() => setTab('calendar')}
            onShowProfile={() => setTab('profile')}
            siteRead={siteRead}
            business={session.tenant.name}
            timezoneId={session.tenant.timezoneId}
            canManage={canManage}
            focus={focus}
            onFocus={setFocus}
          />
        )}

        {tab === 'posts' && (
          <Posts
            canManage={canManage}
            business={session.tenant.name}
            timezoneId={session.tenant.timezoneId}
            refreshToken={postsToken}
            onScheduled={() => {
              setCalendarToken((token) => token + 1)
              refreshSession()
            }}
            onAskDesigner={askDesigner}
          />
        )}

        {tab === 'calendar' && (
          <Calendar
            canManage={canManage}
            business={session.tenant.name}
            refreshToken={calendarToken}
            onChanged={() => {
              setCalendarToken((token) => token + 1)
              refreshSession()
            }}
            onShowChat={() => setTab('chat')}
            onAskDesigner={askDesigner}
          />
        )}

        {tab === 'analytics' && <Analytics session={session} />}

        {tab === 'profile' && (
          <>
            <section className="card">
              <h2>{session.tenant.name}</h2>
              <p className="muted small">
                {t('dashboard.signedInAs', { name: session.user.displayName ?? session.user.email })}
                {' · '}
                {t(`roles.${session.tenant.role}`)}
              </p>

              <div className="account__credits">
                <p className="credits">{t('dashboard.credits', { count: session.credits })}</p>
                {session.allowance && (
                  <p className="muted small">
                    {t('dashboard.slots', {
                      open: session.allowance.openPosts,
                      free: session.allowance.freeSlots,
                    })}
                  </p>
                )}
              </div>
              {session.expiring && (
                <p className="muted small">
                  {t('dashboard.expiring', {
                    count: session.expiring.qty,
                    date: new Date(session.expiring.at).toLocaleDateString(i18n.language),
                  })}
                </p>
              )}

              <div className="fields fields--two">
              {tenants.length > 1 && (
                <label className="field">
                  {t('dashboard.business')}
                  <select
                    value={session.tenant.id}
                    disabled={busy}
                    onChange={(event) =>
                      run(async () => onSessionChanged(await api.switchTenant(event.target.value)))
                    }
                  >
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="field">
                {t('language.label')}
                <div className="languages" role="group" aria-label={t('language.label')}>
                  {SUPPORTED_LANGUAGES.map((language) => (
                    <button
                      key={language}
                      type="button"
                      className={i18n.resolvedLanguage === language ? 'is-active' : ''}
                      onClick={() => i18n.changeLanguage(language)}
                    >
                      {t(`language.${language}`)}
                    </button>
                  ))}
                </div>
              </div>
              </div>

              {/* Leaving is a different kind of decision from the settings above it. */}
              <div className="row row--form account__out">
                <Button
                  type="button"
                  className="button button--quiet"
                  loading={working === 'signOut'}
                  disabled={busy}
                  onClick={signOut}
                >
                  {t('dashboard.signOut')}
                </Button>
                <Button
                  type="button"
                  className="link"
                  disabled={busy}
                  loading={working === 'signOutEverywhere'}
                  onClick={() => run(() => api.logoutEverywhere().then(onSignedOut), 'signOutEverywhere')}
                >
                  {t('dashboard.signOutEverywhere')}
                </Button>
              </div>
            </section>

            {session.user.email && !session.user.emailVerified && (
              <section className="card">
                <p className="status">{t('dashboard.emailUnverified')}</p>

                {verification === null ? (
                  <Button
                    type="button"
                    className="link"
                    disabled={busy}
                    loading={working === 'verify'}
                    onClick={() => run(async () => setVerification(await api.resendVerification()), 'verify')}
                  >
                    {t('dashboard.resendVerification')}
                  </Button>
                ) : (
                  <p className="muted small">
                    {t(verification.sent ? 'dashboard.verificationSent' : 'dashboard.verificationCannotSend')}
                  </p>
                )}
              </section>
            )}

            {session.user.metaReconnectNeeded && (
              <section className="card">
                <p className="status status--bad">{t('dashboard.reconnectFacebook')}</p>
              </section>
            )}

            {canManage && <Accounts facebookEnabled={config?.facebookLogin?.enabled} />}

            {canManage && <Profile onPostsWritten={refresh} />}

            <section className="card">
              <h2>{t('sites.title')}</h2>
              <p className="muted">{t('sites.help')}</p>

              <div className="row row--form">
                <input
                  type="url"
                  value={url}
                  placeholder="https://example.mk"
                  onChange={(event) => setUrl(event.target.value)}
                />
                <Button
                  type="button"
                  className="button button--quiet"
                  disabled={busy || !url}
                  loading={working === 'check'}
                  onClick={checkSite}
                >
                  {t('sites.check')}
                </Button>
                {canManage && (
                  <Button
                    type="button"
                    className="button"
                    disabled={busy || !url}
                    loading={working === 'add'}
                    onClick={addSite}
                  >
                    {t('sites.add')}
                  </Button>
                )}
              </div>

              {check && (
                <p className="status">
                  {check.verdict === 'counted'
                    ? t('sites.counted', { count: check.products, source: check.source })
                    : t(`sites.verdict.${check.verdict}`)}
                </p>
              )}

              {error && <p className="status status--bad">{error}</p>}

              {sites.length > 0 && (
                <div className="table-scroll">
                  <table className="sites">
                    <thead>
                      <tr>
                        <th>{t('sites.column.url')}</th>
                        <th>{t('sites.column.status')}</th>
                        <th>{t('sites.column.products')}</th>
                        <th>{t('sites.column.coverage')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {sites.map((site) => (
                        <tr key={site.id}>
                          <td>{site.url}</td>
                          <td>
                            <span className={site.status === 'Failed' ? 'status status--bad' : 'status'}>
                              {t(`sites.status.${site.status}`)}
                            </span>
                            {site.lastNotes?.length > 0 ? (
                              <ul className="sitenotes">
                                {site.lastNotes.map((note, index) => (
                                  <li key={`${note.code}-${index}`}>{siteNoteText(t, note)}</li>
                                ))}
                              </ul>
                            ) : (
                              /* A read that failed by throwing has a message and no notes. It is
                                 English and there is nothing to be done about that — but it is the
                                 rare case now, not the one every owner sees. */
                              site.lastError && <div className="muted small">{site.lastError}</div>
                            )}
                          </td>
                          <td>{site.productCount}</td>
                          <td>
                            {site.coverageScore === null || site.coverageScore === undefined
                              ? '—'
                              : `${Math.round(site.coverageScore * 100)}%`}
                          </td>
                          <td>
                            {canManage && (
                              <span className="row row--form">
                                <Button
                                  type="button"
                                  className="button button--quiet"
                                  disabled={busy || RUNNING.has(site.status)}
                                  loading={working === `reread:${site.id}`}
                                  onClick={() => run(() => api.ingestSite(site.id).then(refresh), `reread:${site.id}`)}
                                >
                                  {t('sites.reread')}
                                </Button>
                                <button
                                  type="button"
                                  className="link link--danger"
                                  disabled={busy}
                                  onClick={() => setPendingDelete(site)}
                                >
                                  {t('common.delete')}
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <ConfirmDialog
              open={pendingDelete !== null}
              title={t('sites.deleteTitle')}
              body={t('sites.deleteBody', { url: pendingDelete?.url, count: pendingDelete?.productCount ?? 0 })}
              confirmLabel={t('common.delete')}
              destructive
              onCancel={() => setPendingDelete(null)}
              onConfirm={() => {
                const site = pendingDelete
                setPendingDelete(null)
                run(() => api.deleteSite(site.id).then(refresh))
              }}
            />
          </>
        )}
      </main>

      <ReadingBar
        onFinished={() => {
          refresh()
          setSiteRead((token) => token + 1)
        }}
      />

      <nav className="tabs" aria-label={t('tabs.label')}>
        {[
          ['chat', ChatIcon],
          ['posts', PostsIcon],
          ['calendar', CalendarIcon],
          ['analytics', AnalyticsIcon],
          ['profile', ProfileIcon],
        ].map(([name, Icon]) => (
          <button
            key={name}
            type="button"
            className={tab === name ? 'tab is-active' : 'tab'}
            aria-current={tab === name ? 'page' : undefined}
            onClick={() => setTab(name)}
          >
            <span className="tab__icon">
              <Icon />
            </span>
            <span className="tab__label">{t(`tabs.${name}`)}</span>
          </button>
        ))}
      </nav>
    </div>
  )
}
